import React from "react";
import {Alert, Button, Col, Row} from "react-bootstrap";
import {Bar, BarChart, CartesianGrid, Cell, ErrorBar, Tooltip, XAxis, YAxis} from "recharts";
import FileSaver from "file-saver";
import {CurrentPngProps} from "recharts-to-png";
import { ArrowDown, ArrowUp, Box2Fill, Check, CloudDownload, Square } from "react-bootstrap-icons";

interface GenGenPlateCalculatedResultChartProps extends CurrentPngProps {
    chartData: any[]
}

interface GenGenPlateCalculatedResultChartState {
    barChartIdsDisplaying: Set<string>;
    barChartIDOrder: string[];
}

// Schema of this object need to conform to BarChart data format of rechart
interface BarChartItem {
    id: string;
    name: string;
    category: string;
    avg: number;
    errors: any;
    data: any;
}

const chartColor: Map<string, string> = new Map<string, string>([
    ["ntc_plasmid", "#ff9307d6"],
    ["indel_control", "#6c757d"],
    ["positive_control", "#cf2d04"],
    ["single_guide_plasmid", "#28a745"],
    ["combo_plasmid", "#007bff"],
])
const chartErrorBarColour: string = "#6f42c1"

export class GenGenPlateCalculatedResultChart extends React.Component<GenGenPlateCalculatedResultChartProps, GenGenPlateCalculatedResultChartState> {
    constructor(props: GenGenPlateCalculatedResultChartProps) {
        super(props);
        const initialItems = this.buildBarCharts(props.chartData || []);
        const initialIds: string[] = initialItems.map((i) => i.id);
        this.state = {
            barChartIdsDisplaying: new Set<string>(initialIds),
            barChartIDOrder: initialIds
        };
    }

    componentDidUpdate(prevProps: Readonly<GenGenPlateCalculatedResultChartProps>): void {
        if (prevProps.chartData !== this.props.chartData) {
            const newItems = this.buildBarCharts(this.props.chartData || []);
            const newIds: string[] = newItems.map((i) => i.id);
            const newIdsSet: Set<string> = new Set<string>(newIds);
            if (newIds.length === 0) {
                this.setState({barChartIDOrder: [], barChartIdsDisplaying: new Set<string>()});
                return;
            }
            this.setState({barChartIDOrder: newIds, barChartIdsDisplaying: newIdsSet});
        }
    }

    // Build items from data for the chart. Each item represents a bar in the chart.
    buildBarCharts(data: any[]): Array<BarChartItem> {
        const counterByKey: Map<string, number> = new Map<string, number>();
        const items: Array<BarChartItem> = [];

        (data || []).forEach((d: any) => {
            const name: string = d.name;
            const category: string = d.category;
            const key = `${name}|${category}`;
            const occurrence = counterByKey.get(key) ?? 0;
            counterByKey.set(key, occurrence + 1);
            const id = `${key}|${occurrence}`;
            items.push({id, name, category, avg: d.avg, errors: d.errors, data: d});
        });

        return items;
    }

    handleDownload = async () => {
        const png = await this.props.getPng();
        if (png) {
            FileSaver.saveAs(png, "chart.png");
        }
    };

    toggleBar(id: string): void {
        const next = new Set<string>(this.state.barChartIdsDisplaying);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        this.setState({
            barChartIdsDisplaying: next
        });
    };

    selectAll = () => {
        const allIds: string[] = this.buildBarCharts(this.props.chartData || []).map((i) => i.id);
        this.setState({barChartIdsDisplaying: new Set<string>(allIds)});
    };

    deselectAll = () => {
        this.setState({barChartIdsDisplaying: new Set<string>()});
    };

    moveUp(id: string): void {
        const nextOrder = [...this.state.barChartIDOrder];
        const idx = nextOrder.indexOf(id);
        if (idx > 0) {
            const tmp = nextOrder[idx - 1];
            nextOrder[idx - 1] = nextOrder[idx];
            nextOrder[idx] = tmp;
        }
        this.setState({barChartIDOrder: nextOrder});
    };

    moveDown(id: string): void {
        const nextOrder = [...this.state.barChartIDOrder];
        const idx = nextOrder.indexOf(id);
        if (idx > -1 && idx < nextOrder.length - 1) {
            const tmp = nextOrder[idx + 1];
            nextOrder[idx + 1] = nextOrder[idx];
            nextOrder[idx] = tmp;
        }
        this.setState({barChartIDOrder: nextOrder});
    };

    render() {
        const barCharts = this.buildBarCharts(this.props.chartData || []);
        const barChartsByIds: Map<string, BarChartItem> = new Map<string, BarChartItem>(barCharts.map((barChart) => [barChart.id, barChart]));
        const displayedBarChartsData: BarChartItem[] = this.state.barChartIDOrder
            .filter((id) => this.state.barChartIdsDisplaying.has(id))
            .map((id) => barChartsByIds.get(id)!.data) ?? [];

        return <>
            <Row className={"mt-2"}>
                <Col md={5} className={"mt-2"}>
                    <div className={"mb-2"}>
                        <Button size={"sm"} variant={"secondary"} className={"me-2"} onClick={this.selectAll}><Check /> Select All</Button>
                        <Button size={"sm"} variant={"outline-secondary"} className={"me-2"} onClick={this.deselectAll}><Square /> Deselect All</Button>
                        <Button size={"sm"} onClick={this.handleDownload.bind(this)} disabled={this.props.isLoading}>
                            <CloudDownload /> {this.props.isLoading ? "Downloading..." : "Download Chart"}
                        </Button>
                    </div>
                    {
                        Array.from(chartColor.keys()).map((category) =>
                            <span key={category} className={"badge me-2"} style={{backgroundColor: chartColor.get(category)}}>{category}</span>
                        )
                    }
                </Col>
                <Col md={7}>
                    <div style={{maxHeight: "220px", overflowY: "auto", border: "1px solid #e9ecef", borderRadius: 4, padding: 8}}>
                        {
                            this.state.barChartIDOrder.map((id: string, idx: number) => {
                                const item = barChartsByIds.get(id);
                                if (!item) return <Alert key={`control-${id}`} variant={"danger"}>Bar chart data not found for id: {id}</Alert>;
                                return (
                                    <div key={`control-${id}`} className={"d-flex align-items-center mb-1"}>
                                        <input
                                            type={"checkbox"}
                                            className={"form-check-input me-2"}
                                            checked={this.state.barChartIdsDisplaying.has(id)}
                                            onChange={() => this.toggleBar(id)}
                                        />
                                        <span className={"badge me-2"} style={{backgroundColor: chartColor.get(item.category)}}>{item.name}</span>
                                        <Button size={"sm"} variant={"light"} className={"me-1"} disabled={idx === 0} onClick={() => this.moveUp(id)}><ArrowUp/></Button>
                                        <Button size={"sm"} variant={"light"} disabled={idx === this.state.barChartIDOrder.length - 1} onClick={() => this.moveDown(id)}><ArrowDown/></Button>
                                    </div>
                                );
                            })
                        }
                    </div>
                </Col>
                <Col md={12}>
                    <div style={{overflowX: "scroll"}}>
                        <BarChart
                            id={"bar-chart"}
                            width={1000}
                            height={450}
                            data={displayedBarChartsData}
                            ref={this.props.chartRef}
                            style={{
                                fontSize: "0.6rem"
                            }}
                        >
                            <CartesianGrid strokeDasharray="1 1"/>
                            {/*Interval = 0 will show bar name for all bar */}
                            <XAxis dataKey="name" angle={-22.5} textAnchor="end" height={120} interval={0}/>
                            <YAxis/>
                            <Tooltip label={"name"}/>
                            <Bar dataKey="avg" fill="#82ca9d" barSize={20}>
                                <ErrorBar dataKey={"errors"} stroke={chartErrorBarColour}/>
                                {
                                    displayedBarChartsData.map((barChartData: any) => {
                                        const key = `${barChartData.name}|${barChartData.category}`;
                                        return <Cell
                                            key={`gen-gen-plate-cell-${key}`}
                                            fill={chartColor.get(barChartData.category)}
                                            type={barChartData.category}/>
                                    })
                                }
                            </Bar>
                        </BarChart>
                    </div>
                </Col>
            </Row>
        </>;
    }
}